import React from "react";
import type { DiscoveryInfluencerProfile } from "../../types/discoveryProfile.types";

export interface OverviewTabProps {
  influencer: DiscoveryInfluencerProfile;
}

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

export function OverviewTab({ influencer }: OverviewTabProps) {
  const metrics = [
    {
      label: "Total Engagement",
      value: formatNumber(influencer.totalEngagement),
      imgSrc: "/images/Dashboard/engagement.png",
      hasWatermark: false,
      imgClass: "w-[195px] h-[100px]",
      sizeClass: "w-full md:w-[550px] h-[200px]",
    },
    {
      label: "Average Views",
      value: formatNumber(influencer.avgViews),
      imgSrc: "/images/Dashboard/views.png",
      hasWatermark: true,
      imgClass: "w-[64px] h-[50px]",
      sizeClass: "w-full md:w-[500px] h-[160px]",
    },
    {
      label: "Average Likes",
      value: formatNumber(influencer.avgLikes),
      imgSrc: "/images/Dashboard/likes.png",
      hasWatermark: true,
      imgClass: "w-[56px] h-[50px]",
      sizeClass: "w-full md:w-[550px] h-[120px]",
    },
    {
      label: "Average Comments",
      value: formatNumber(influencer.avgComments),
      imgSrc: "/images/Dashboard/comments.png",
      hasWatermark: true,
      imgClass: "w-[48px] h-[46px]",
      sizeClass: "w-full md:w-[500px] h-[160px]",
    },
  ];

  const renderCard = (metric: typeof metrics[0], index: string | number) => (
    <div
      key={index}
      className={`relative overflow-hidden bg-white/80 backdrop-blur-md border border-[#D9D9D9] rounded-[20px] p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex items-center justify-between ${metric.sizeClass}`}
    >
      {/* Left Content (Text) */}
      <div className="flex flex-col gap-3 relative z-10">
        <span className="text-2xl font-medium text-[#878397] underline underline-offset-4">
          {metric.label}
        </span>
        <span className="text-[34px] font-bold font-['Inter'] text-[#633A89] leading-none mt-3">
          {metric.value}
        </span>
      </div>

      {/* Right Content (Main Image/Icon) */}
      <div className="relative z-10 flex items-center justify-center">
        <img
          src={metric.imgSrc}
          alt={metric.label}
          className={`object-contain ${metric.imgClass} mr-6`}
        />
      </div>

      {/* Faint Watermark Image (Bottom Right) */}
      {metric.hasWatermark && (
        <img
          src={metric.imgSrc}
          alt=""
          className="absolute -right-8 -bottom-8 -rotate-12 w-32 h-32 object-contain opacity-[0.1] pointer-events-none"
        />
      )}
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row items-start gap-4 p-5">
      {/* Left Column: Engagement (0) and Likes (2) */}
      <div className="flex flex-col gap-4">
        {[metrics[0], metrics[2]].map((metric, i) => renderCard(metric, `left-${i}`))}
      </div>

      {/* Right Column: Views (1) and Comments (3) */}
      <div className="flex flex-col gap-4">
        {[metrics[1], metrics[3]].map((metric, i) => renderCard(metric, `right-${i}`))}
      </div>
    </div>
  );
}

export default OverviewTab;
